import { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import {
  Drawer,
  Button,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Radio,
  Checkbox,
  Upload,
  Tooltip,
  message,
} from "antd";
import {
  CloseOutlined,
  CheckOutlined,
  EnvironmentOutlined,
  PlusOutlined,
  DeleteOutlined,
  InboxOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { getSubdomain } from "app/utils/generic.helper";
import "./HotelOnboardingDrawer.scss";

const API_ONBOARDING_SAVE = "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/save";
const API_ONBOARDING_DATA = "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/onBoarding/data";
const API_ONBOARDING_LEGAL_NAME = "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/onBoarding/legalName";
const API_ONBOARDING_SAMPLE_INVOICE_UPLOAD = "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/onBoarding/sampleInvoiceUpload";
const MAX_UPLOAD_SIZE_BYTES = 10 * 1024 * 1024;

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

const YES_NO_NOT_KNOWN = ["Yes", "No", "Not known"];

const PROPERTY_FIELDS = [
  { name: "propertyName", label: "Property Name", type: "text", required: true, placeholder: "As on signage" },
  {
    name: "chainOrIndependent",
    label: "Chain or Independent",
    type: "select",
    required: true,
    options: ["Independent", "Chain"],
  },
  {
    name: "chainBrandName",
    label: "Chain / Brand Name",
    type: "text",
    placeholder: "If chain",
    showIf: (p) => p.chainOrIndependent === "Chain",
  },
  { name: "propertyAddress", label: "Property Address", type: "longtext", required: true },
  {
    name: "registeredBillingAddress",
    label: "Registered / Billing Address",
    type: "longtext",
    note: "If different from property",
  },
  {
    name: "categoryStarRating",
    label: "Category / Star Rating",
    type: "selectOther",
    options: ["5★", "4★", "3★", "Budget", "Serviced apt", "Not known"],
  },
  { name: "noOfRooms", label: "No. of Rooms", type: "number", note: "Volume proxy" },
  {
    name: "ownershipModel",
    label: "Ownership Model",
    type: "selectOther",
    options: ["Owned", "Managed", "Franchised", "Not known"],
    note: "Affects who invoices",
  },
];

const POC_ROLES = [
  { key: "general_manager", label: "General Manager", required: false },
  { key: "finance", label: "Finance / Accounts POC", required: true, designation: true },
  { key: "billing", label: "Billing / GST / Taxation POC", required: true, note: "The person who actually issues invoices — the real key contact" },
  { key: "revenue", label: "Revenue Manager", required: false },
  { key: "reservations", label: "Reservations / Front Office POC", required: false },
  { key: "nodal_POC_for_Finkraft", label: "Nodal POC for Finkraft", required: true, note: "Single point for follow-ups" },
];

const SECTIONS = [
  {
    key: "ownership",
    title: "Onboarded By",
    shortTitle: "Onboarded By",
    desc: "Who created this record. Also the assessment attribution — but only for Finkraft-assisted onboards.",
    sectionNote:
      "onboarding_source is the master flag. For self-serve, onboarded_by is stored as \"Self-onboarded\" (not a person) so it never counts toward any BA's assignment tally, and the whole person-block is auto-populated rather than shown to the hotel.",
    fields: [
      {
        name: "onboardingSource",
        label: "Onboarding Source",
        type: "select",
        required: true,
        options: ["Finkraft-assisted", "Self-onboarded (hotel via platform)"],
        note: "This choice drives everything below",
      },
      {
        name: "finkraftPocName",
        label: "Finkraft POC Name",
        type: "text",
        required: true,
        placeholder: "Person who onboarded the hotel",
        showIf: (v) => v.onboardingSource === "Finkraft-assisted",
      },
      {
        name: "finkraftPocEmail",
        label: "Finkraft POC Email",
        type: "email",
        required: true,
        placeholder: "name@finkraft.ai",
        showIf: (v) => v.onboardingSource === "Finkraft-assisted",
      },
      {
        name: "finkraftPocPhone",
        label: "Finkraft POC Phone",
        type: "phone",
        required: true,
        placeholder: "+91 XXXXX XXXXX",
        showIf: (v) => v.onboardingSource === "Finkraft-assisted",
      },
      {
        name: "cityTerritory",
        label: "City / Territory",
        type: "selectOther",
        required: true,
        options: ["Mumbai", "Delhi-NCR", "Bengaluru", "Not known"],
      },
      {
        name: "onboardingDate",
        label: "Onboarding Date",
        type: "date",
        required: true,
        note: "Visit date (assisted) or self-signup date (self-serve)",
      },
      {
        name: "mode",
        label: "Mode",
        type: "selectOther",
        required: true,
        options: ["In-person visit", "Call", "Email", "Self-serve (platform)", "Not known"],
      },
      {
        name: "geolocation",
        label: "Geolocation",
        type: "geo",
        required: true,
        note: "Auto-capture, or paste a Google Maps link if Capture doesn't work",
      },
      {
        name: "onboardingStatus",
        label: "Onboarding Status",
        type: "select",
        required: true,
        options: ["Interested", "Onboarded", "Follow-up needed", "Declined"],
      },
    ],
  },
  {
    key: "identity",
    title: "Hotel Identity & Legal",
    shortTitle: "Identity & Legal",
    desc: "The anchor for recovery and dedupe.",
    fields: [
      {
        name: "legalBillingEntityName",
        label: "Legal / Billing Entity Name",
        type: "text",
        required: true,
        fullWidth: true,
        note: "The entity that raises invoices — often ≠ brand",
      },
      { name: "gstins", label: "GSTIN(s)", type: "repeatable", required: true, note: "One or more; validate format" },
      { name: "pan", label: "PAN", type: "text" },
      { name: "properties", type: "propertyGroup" },
    ],
  },
  {
    key: "contacts",
    title: "Hotel Contact Mapping",
    shortTitle: "Contact Mapping",
    desc: "Capture each role separately; the billing/GST POC matters most for recovery.",
    fields: [
      { name: "pocRoles", type: "pocGroup" },
      {
        name: "preferredChannel",
        label: "Preferred Channel",
        type: "selectOther",
        options: ["Email", "Phone", "WhatsApp", "Portal", "Not known"],
      },
    ],
  },
  {
    key: "technology",
    title: "Technology Mapping",
    shortTitle: "Technology",
    desc: "Determines whether/how we can integrate vs. chase manually.",
    fields: [
      {
        name: "pms",
        label: "PMS",
        type: "selectOther",
        required: true,
        options: ["Opera", "IDS Next", "Hotelogix", "eZee", "Cloudbeds", "RMS", "Protel", "WinHMS", "Shawman", "Djubo", "mycloud", "Not known"],
      },
      { name: "pmsVersion", label: "PMS Version", type: "text" },
      { name: "billingPosSystem", label: "Billing / POS System", type: "text", note: "If separate from PMS" },
      {
        name: "accountingErp",
        label: "Accounting / ERP",
        type: "selectOther",
        options: ["Tally", "SAP", "Zoho Books", "Busy", "Not known"],
      },
      { name: "einvoicingGenerated", label: "E-invoicing (IRN) Generated?", type: "select", required: true, options: YES_NO_NOT_KNOWN },
      {
        name: "einvoicingTool",
        label: "E-invoicing Tool / GSP-ASP",
        type: "selectOther",
        options: ["ClearTax", "IRIS", "Masters India", "Zoho", "TallyPrime", "GSTN portal direct"],
      },
      {
        name: "invoiceGeneration",
        label: "Invoice Generation",
        type: "selectOther",
        required: true,
        options: ["System-generated PDF", "Manual", "Mixed", "Not known"],
      },
      { name: "sendingDomain", label: "Sending Domain for Invoices", type: "email", placeholder: "e.g. accounts@hotel.com" },
      {
        name: "integrationCapability",
        label: "Integration Capability",
        type: "selectOther",
        options: ["Has API", "Portal export", "None", "Not known"],
      },
      { name: "existingPortal", label: "Existing Corporate/Invoice Portal?", type: "yesnotext", notePlaceholder: "Name/URL if yes" },
    ],
  },
  {
    key: "revenue",
    title: "Corporate Booking & Revenue Flow",
    shortTitle: "Revenue Flow",
    desc: "How corporate demand reaches the hotel — where recoverable invoices originate.",
    fields: [
      {
        name: "corporateBookingChannels",
        label: "Corporate Booking Channels",
        type: "multiselect",
        required: true,
        options: ["Direct", "Corporate self-booking tool", "TMC or travel agent", "OTA", "GDS", "Aggregator", "Walk-in"],
      },
      {
        name: "majorOtasUsed",
        label: "Major Channel Partner",
        type: "multiselect",
        noSelectAll: true,
        groupedOptions: [
          {
            label: "OTA",
            options: ["MakeMyTrip (MMT)", "Booking.com", "Agoda", "Expedia", "Cleartrip", "Pickyourtrail", "Other (OTA)"],
          },
          {
            label: "TMC",
            options: ["FCM Travel", "BCD Travel", "ATPI", "CWT", "Amex GBT", "Quest2Travel", "Other (TMC)"],
          },
        ],
      },
      {
        name: "pctCorporateVsRetail",
        label: "Approx % Corporate vs Retail",
        type: "selectOther",
        options: ["<10%", "10-30%", "30-60%", ">60%", "Not known"],
      },
      {
        name: "monthlyCorporateRoomNights",
        label: "Est. Monthly Corporate Room Nights",
        type: "selectOther",
        options: ["<50", "50-200", "200-500", ">500", "Not known"],
      },
      {
        name: "corporateRatePlans",
        label: "Corporate Rate Plans",
        type: "multiselect",
        options: ["Negotiated", "Last Room Availability (LRA)", "Best Available Rate (BAR) only", "Not known", "Other"],
      },
      {
        name: "corporateVsRetailFlagged",
        label: "Corporate vs Retail Flagged at Booking?",
        type: "yesno",
        note: "Affects whether GST invoice is issued correctly",
      },
      { name: "majorCorporatesTmcs", label: "Major Corporates / TMCs Served", type: "longtext", note: "Free text list" },
    ],
  },
  {
    key: "invoicing",
    title: "Invoicing & GST Practices",
    shortTitle: "Invoicing & GST",
    desc: "The core recovery problem.",
    fields: [
      {
        name: "issuesGstInvoice",
        label: "Issues GST Tax Invoice for Corporate Stays?",
        type: "selectOther",
        required: true,
        options: ["Always", "Sometimes", "Only on request", "Never", "Not known"],
      },
      {
        name: "capturesGuestCompanyGstin",
        label: "Captures Guest Company GSTIN at Booking/Check-in?",
        type: "selectOther",
        required: true,
        options: ["Always", "Sometimes", "Never", "Not known"],
      },
      {
        name: "invoiceTiming",
        label: "Invoice Timing",
        type: "selectOther",
        options: ["At checkout", "Monthly", "On request", "Not known"],
      },
      {
        name: "invoiceConsolidation",
        label: "Per-stay or Consolidated Invoices?",
        type: "selectOther",
        options: ["Per stay", "Consolidated", "Both", "Not known"],
      },
      {
        name: "invoiceFormatCompliance",
        label: "Invoice Format Compliance",
        type: "multiselect",
        options: ["Corporate GSTIN shown", "Place of supply", "HSN-SAC", "Not known"],
      },
      {
        name: "howInvoicesShared",
        label: "How Invoices are Shared with Corporates",
        type: "multiselect",
        required: true,
        options: ["Email manual", "Email auto", "Vendor portal upload", "Download link", "TMC or agent", "WhatsApp", "Courier", "Only on request"],
      },
      {
        name: "typicalTurnaround",
        label: "Typical Turnaround on Invoice Requests",
        type: "selectOther",
        options: ["Same day", "2-3 days", ">1 week", "Unreliable", "Not known"],
      },
      { name: "creditNotesHandled", label: "Credit Notes / Corrections Handled?", type: "yesnotext" },
    ],
  },
  {
    key: "payment",
    title: "Payment Flow",
    shortTitle: "Payment Flow",
    desc: "Where reconciliation and GST mismatches happen.",
    fields: [
      {
        name: "corporatePaymentModes",
        label: "Corporate Payment Modes Accepted",
        type: "multiselect",
        required: true,
        options: ["Virtual card", "Lodge-BTA card", "Direct billing (BTC/credit)", "Bank transfer", "Corporate credit card", "UPI", "Cash"],
      },
      { name: "acceptsVirtualCards", label: "Accepts Virtual Cards?", type: "selectOther", required: true, options: YES_NO_NOT_KNOWN },
      {
        name: "virtualCardProviders",
        label: "Virtual Card Providers",
        type: "multiselect",
        noSelectAll: true,
        options: ["Conferma", "AirPlus", "eNett", "WEX", "Nium", "HRS"],
      },
      {
        name: "virtualCardMismatch",
        label: "Do Virtual-card Payments Cause Invoicing/GST Mismatch?",
        type: "select",
        options: YES_NO_NOT_KNOWN,
      },
      {
        name: "paymentTermsCreditPeriod",
        label: "Payment Terms / Credit Period",
        type: "selectOther",
        options: ["Advance", "On checkout", "15d", "30d", "45d+", "Not known"],
      },
      {
        name: "reconciliationMethod",
        label: "Reconciliation Method",
        type: "selectOther",
        options: ["Manual", "System", "Via TMC", "Not known"],
      },
    ],
  },
  {
    key: "painpoints",
    title: "Pain Points & Platform Opportunity",
    shortTitle: "Pain Points",
    desc: "Open questions — this is the product discovery layer.",
    fields: [
      { name: "topIssuesBookings", label: "Top Issues with Corporate Bookings", type: "longtext" },
      { name: "topIssuesPayments", label: "Top Issues with Corporate Payments", type: "longtext" },
      { name: "invoiceDisputes", label: "Common Invoice Disputes / Missing-GST Situations", type: "longtext" },
      { name: "invoiceSharingImprovement", label: "What Would Make Invoice-sharing Easier for Them?", type: "longtext" },
      {
        name: "willingnessShareFinkraft",
        label: "Willingness to Share Invoices via Finkraft",
        type: "select",
        required: true,
        options: ["High", "Medium", "Low", "Refused"],
      },
      {
        name: "willingnessIntegratePms",
        label: "Willingness to Integrate PMS/Portal",
        type: "select",
        options: ["High", "Medium", "Low", "Not discussed"],
      },
    ],
  },
  {
    key: "outcome",
    title: "Onboarding Outcome & Verification",
    shortTitle: "Outcome",
    desc: "Prevents fabricated records; also the assessment scoring input.",
    fields: [
      { name: "onboardedOnPlatform", label: "Onboarded on Platform?", type: "yesno", required: true },
      { name: "invoicesCollected", label: "Invoice(s) Collected?", type: "yesno", required: true },
      { name: "noOfInvoicesCollected", label: "No. of Invoices Collected", type: "number" },
      { name: "invoicePeriodCovered", label: "Invoice Period Covered", type: "text" },
      { name: "sampleInvoiceUpload", label: "Sample Invoice Upload", type: "file", uploadType: "invoiceSample", note: "For authenticity + GSTIN validation (max 10MB)" },
      { name: "visitingCardPhoto", label: "Visiting Card / Signage / POC Photo", type: "file", uploadType: "image", note: "Visit proof (max 10MB)" },
      { name: "gstinValidated", label: "GSTIN Validated", type: "select", options: ["Auto", "Yes", "No"], note: "Auto-filled from GSTIN format check; override if needed" },
      { name: "verificationStatus", label: "Verification Status", type: "select", required: true, options: ["Pending", "Verified", "Flagged"] },
      {
        name: "barrierIfNotCollected",
        label: "Barrier (if not collected)",
        type: "selectOther",
        options: ["Finance unresponsive", "No corporate bookings", "Refused", "Needs approval", "Not known"],
      },
    ],
  },
];

function isRequired(field, values) {
  return typeof field.required === "function" ? field.required(values) : !!field.required;
}

function getProperties(values) {
  return Array.isArray(values.properties) && values.properties.length ? values.properties : [{}];
}

function flattenRequiredFieldNames(section, values) {
  const names = [];
  section.fields.forEach((f) => {
    if (f.type === "divider") return;
    if (f.type === "pocGroup") {
      POC_ROLES.forEach((role) => {
        if (role.required) names.push(`${role.key}:name`, `${role.key}:email`, `${role.key}:phone`);
      });
      return;
    }
    if (f.type === "propertyGroup") {
      getProperties(values).forEach((prop, idx) => {
        PROPERTY_FIELDS.forEach((pf) => {
          if (!isFieldVisible(pf, prop)) return;
          if (isRequired(pf, prop)) {
            names.push(`properties:${idx}:${pf.name}`);
            if (pf.type === "selectOther" && prop[pf.name] === "Other") {
              names.push(`properties:${idx}:${pf.name}Other`);
            }
          }
        });
      });
      return;
    }
    if (isRequired(f, values) && isFieldVisible(f, values)) {
      names.push(f.name);
      if (f.type === "selectOther" && values[f.name] === "Other") {
        names.push(`${f.name}Other`);
      }
    }
  });
  return names;
}

function isFieldVisible(field, values) {
  if (!field.showIf) return true;
  return field.showIf(values);
}

function getFieldValue(values, name) {
  if (name.startsWith("properties:")) {
    const [, idxStr, propField] = name.split(":");
    const prop = getProperties(values)[Number(idxStr)] || {};
    return prop[propField];
  }
  if (name.includes(":")) {
    const [roleKey, suffix] = name.split(":");
    const first = (values[roleKey] && values[roleKey][0]) || {};
    return first[suffix];
  }
  return values[name];
}

function isSectionComplete(section, values) {
  const names = flattenRequiredFieldNames(section, values);
  return names.every((n) => {
    const v = getFieldValue(values, n);
    return v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0);
  });
}

const DATE_FIELD_NAMES = SECTIONS.flatMap((s) => s.fields.filter((f) => f.type === "date").map((f) => f.name));

function hydrateValues(values) {
  const next = { ...values };
  DATE_FIELD_NAMES.forEach((name) => {
    if (next[name]) next[name] = dayjs(next[name]);
  });
  return next;
}

const SELF_ONBOARDED_VALUE = "Self-onboarded (hotel via platform)";
const SELF_ONBOARDED_NAME = "Self-onboarded";

function getProgressColor(percent) {
  if (percent >= 80) return "#1a9c6b";
  if (percent >= 40) return "#e39a1f";
  return "#d0433f";
}

const GSTIN_PATTERN = /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

function GstinRepeater({ value = [""], onChange, disabled }) {
  const list = value.length ? value : [""];
  return (
    <div className="hod-repeater">
      {list.map((v, i) => {
        const invalid = !!v && !GSTIN_PATTERN.test(v);
        return (
          <div key={i}>
            <div className="hod-repeater-row">
              <Input
                className="hod-input"
                placeholder="15-digit GSTIN"
                value={v}
                disabled={disabled}
                maxLength={15}
                status={invalid ? "error" : undefined}
                onChange={(e) => {
                  const next = [...list];
                  next[i] = e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, "").slice(0, 15);
                  onChange(next);
                }}
              />
              {list.length > 1 && (
                <Button
                  type="text"
                  danger
                  disabled={disabled}
                  icon={<DeleteOutlined />}
                  onClick={() => onChange(list.filter((_, idx) => idx !== i))}
                />
              )}
            </div>
            {invalid && <div className="hod-field-error">Invalid GSTIN format</div>}
          </div>
        );
      })}
      <Button
        type="link"
        className="hod-add-link"
        disabled={disabled}
        icon={<PlusOutlined />}
        onClick={() => onChange([...list, ""])}
      >
        Add GSTIN
      </Button>
    </div>
  );
}

// Reads lat/lng out of a pasted Google Maps link (or plain "lat, lng" text) —
// a no-API-key fallback for systems where navigator.geolocation doesn't work.
// Tries, in order: the precise place marker (!3d..!4d..), the map-center
// pin in the URL path (@lat,lng,zoom), the q=/query=/ll= search param, then
// finally plain "lat, lng" text typed/pasted directly.
function parseGeoInput(text) {
  if (!text) return null;
  const trimmed = text.trim();
  const coord = "(-?\\d{1,3}\\.\\d+)";
  let m = trimmed.match(new RegExp(`!3d${coord}!4d${coord}`));
  if (m) return `${m[1]}, ${m[2]}`;
  m = trimmed.match(new RegExp(`@${coord},${coord}`));
  if (m) return `${m[1]}, ${m[2]}`;
  m = trimmed.match(new RegExp(`[?&](?:q|query|ll)=${coord},${coord}`));
  if (m) return `${m[1]}, ${m[2]}`;
  m = trimmed.match(new RegExp(`^${coord}\\s*,\\s*${coord}$`));
  if (m) return `${m[1]}, ${m[2]}`;
  return null;
}

function GeoCapture({ value, onChange, disabled }) {
  const [capturing, setCapturing] = useState(false);
  const [draft, setDraft] = useState(value || "");

  useEffect(() => {
    setDraft(value || "");
  }, [value]);

  function capture() {
    if (!navigator.geolocation) {
      message.error("Geolocation not supported on this device.");
      return;
    }
    setCapturing(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        onChange(`${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)}`);
        setCapturing(false);
      },
      () => {
        message.error("Could not fetch location.");
        setCapturing(false);
      }
    );
  }

  function commitDraft() {
    const trimmed = draft.trim();
    if (!trimmed) {
      onChange("");
      return;
    }
    const parsed = parseGeoInput(trimmed);
    if (parsed) {
      onChange(parsed);
      setDraft(parsed);
    } else {
      message.error("Couldn't read coordinates from that. Paste a Google Maps link or \"lat, lng\".");
      setDraft(value || "");
    }
  }

  return (
    <div className="hod-geo">
      <Input
        className="hod-input"
        value={draft}
        allowClear
        disabled={disabled}
        placeholder="Not captured"
        onChange={(e) => {
          const v = e.target.value;
          setDraft(v);
          if (!v) onChange("");
        }}
        onBlur={commitDraft}
        onPressEnter={commitDraft}
      />
      <Button
        className="hod-geo-btn"
        icon={<EnvironmentOutlined />}
        loading={capturing}
        disabled={disabled}
        onClick={capture}
      >
        Capture
      </Button>
      <Tooltip title="If Capture doesn't work on your device, paste a Google Maps link into the field instead (e.g. from Maps' Share > Copy link) — we'll pull the coordinates out of it.">
        <InfoCircleOutlined className="hod-geo-info" />
      </Tooltip>
    </div>
  );
}

function urlToFileItem(url) {
  if (!url) return null;
  const name = decodeURIComponent(url.split("/").pop() || url);
  return { uid: url, name, status: "done", url };
}

function FileField({ value, onChange, disabled, uploadType }) {
  // `value` is an array of plain uploaded URL strings (that's what gets
  // saved in the payload); this local list is only for antd's own item
  // rendering. Several files can be added — each uploads independently.
  const [fileList, setFileList] = useState(() => {
    const arr = Array.isArray(value) ? value : value ? [value] : [];
    return arr.map((v) => (typeof v === "string" ? urlToFileItem(v) : v)).filter(Boolean);
  });

  function commit(next) {
    setFileList(next);
    const items = next.map((f) => f.rawFile || f.url).filter(Boolean);
    onChange(items.length ? items : null);
  }

  async function customUpload({ file, onSuccess, onError }) {
    if (!uploadType) {
      // No upload endpoint wired for this field yet — keep the old
      // local-only behavior (files just sit in form state, unsaved).
      setFileList((prev) => {
        const next = [...prev, { uid: file.uid, name: file.name, status: "done", rawFile: file }];
        onChange(next.map((f) => f.rawFile || f.url).filter(Boolean));
        return next;
      });
      onSuccess({}, file);
      return;
    }
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("filename", file.name);
      formData.append("gstin", localStorage.getItem("hotelGstin") || "");
      formData.append("type", uploadType);
      const res = await fetch(API_ONBOARDING_SAMPLE_INVOICE_UPLOAD, {
        method: "POST",
        body: formData,
      });
      const result = await res.json().catch(() => null);
      if (!res.ok || result?.status === false) {
        throw new Error(result?.message || "Upload failed");
      }
      const url = result?.url || result?.data?.url || null;
      if (url) {
        // Functional update so concurrent uploads (multiple files dropped at
        // once) each append to the latest list instead of racing each other.
        setFileList((prev) => {
          const next = [...prev, urlToFileItem(url)];
          const urls = next.map((f) => f.url).filter(Boolean);
          onChange(urls);
          return next;
        });
      }
      onSuccess(result, file);
    } catch (error) {
      console.error("[FileField] upload failed:", error);
      message.error("Upload failed. Please try again.");
      onError(error);
    }
  }

  return (
    <Upload.Dragger
      className="hod-upload"
      multiple
      disabled={disabled}
      beforeUpload={(file) => {
        if (file.size > MAX_UPLOAD_SIZE_BYTES) {
          message.error(`"${file.name}" is larger than 10MB. Please upload a smaller file.`);
          return Upload.LIST_IGNORE;
        }
        return true;
      }}
      customRequest={customUpload}
      fileList={fileList}
      onRemove={(file) => commit(fileList.filter((f) => f.uid !== file.uid))}
    >
      <InboxOutlined className="hod-upload-icon" />
      <div className="hod-upload-text">Click or drag file to upload</div>
    </Upload.Dragger>
  );
}

function Field({ field, values, setValue, disabled, errors }) {
  if (!isFieldVisible(field, values)) return null;
  const value = values[field.name];
  const onChange = (v) => setValue(field.name, v);
  const emailFormatInvalid = field.type === "email" && !!value && !EMAIL_PATTERN.test(value);
  const fieldErrorText = emailFormatInvalid
    ? "Please enter a valid email address"
    : errors?.[field.name] === "Required"
    ? "Required"
    : errors?.[field.name] || null;

  let input = null;
  switch (field.type) {
    case "text":
      input = (
        <Input
          className="hod-input"
          placeholder={field.placeholder}
          value={value || ""}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      );
      break;
    case "email":
      input = (
        <Input
          className="hod-input"
          status={fieldErrorText ? "error" : undefined}
          placeholder={field.placeholder}
          value={value || ""}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      );
      break;
    case "phone":
      input = (
        <Input
          className="hod-input"
          placeholder={field.placeholder}
          value={value || ""}
          disabled={disabled}
          maxLength={10}
          onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 10))}
          onKeyDown={(e) => {
            const allowedControlKeys = ["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"];
            if (e.ctrlKey || e.metaKey || allowedControlKeys.includes(e.key) || /^[0-9]$/.test(e.key)) return;
            e.preventDefault();
          }}
        />
      );
      break;
    case "longtext":
      input = (
        <Input.TextArea
          className="hod-input"
          rows={3}
          placeholder={field.placeholder}
          value={value || ""}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      );
      break;
    case "number":
      input = (
        <InputNumber
          className="hod-input hod-input-number"
          value={value}
          disabled={disabled}
          onChange={onChange}
        />
      );
      break;
    case "date":
      input = (
        <DatePicker
          className="hod-input"
          classNames={{ popup: { root: "hod-popup" } }}
          value={value}
          disabled={disabled}
          onChange={onChange}
          format="DD-MM-YYYY"
        />
      );
      break;
    case "select":
      input = (
        <Select
          className="hod-input"
          classNames={{ popup: { root: "hod-popup" } }}
          placeholder="Select"
          value={value}
          disabled={disabled}
          options={field.options.map((o) => ({ label: o, value: o }))}
          onChange={onChange}
        />
      );
      break;
    case "multiselect": {
      const selectedArr = Array.isArray(value) ? value : [];
      // groupedOptions lets a multiselect show labeled sections (e.g. OTA vs
      // TMC) instead of one flat list; flatOptions is what "Select All" and
      // the completeness check actually operate on either way.
      const flatOptions = field.groupedOptions
        ? field.groupedOptions.flatMap((g) => g.options)
        : field.options;
      const allSelected = flatOptions.length > 0 && flatOptions.every((o) => selectedArr.includes(o));
      const selectAllOption = { label: allSelected ? "✓ All Selected" : "Select All", value: "__all__" };
      const groupOptions = field.groupedOptions
        ? field.groupedOptions.map((g) => ({
            label: g.label,
            title: g.label,
            options: g.options.map((o) => ({ label: o, value: o })),
          }))
        : flatOptions.map((o) => ({ label: o, value: o }));
      // Any option value starting with "Other" gets its own free-text
      // "Specify" box when selected — e.g. "Other (OTA)" and "Other (TMC)"
      // can both be picked at once, each with its own note.
      const selectedOtherValues = selectedArr.filter((v) => v.startsWith("Other"));
      input = (
        <>
          <Select
            mode="multiple"
            className="hod-input"
            classNames={{ popup: { root: "hod-popup" } }}
            placeholder="Select all that apply"
            value={selectedArr}
            disabled={disabled}
            options={field.noSelectAll ? groupOptions : [selectAllOption, ...groupOptions]}
            onChange={(v) => {
              if (Array.isArray(v) && v.includes("__all__")) {
                onChange(allSelected ? [] : flatOptions);
              } else {
                onChange(v);
              }
            }}
          />
          {selectedOtherValues.map((otherVal) => {
            const otherKey = `${field.name}__${otherVal.replace(/[^a-zA-Z0-9]/g, "")}`;
            return (
              <Input
                key={otherVal}
                className="hod-input hod-input-mt"
                placeholder={`Specify — ${otherVal}`}
                value={values[otherKey] || ""}
                disabled={disabled}
                onChange={(e) => setValue(otherKey, e.target.value)}
              />
            );
          })}
        </>
      );
      break;
    }
    case "selectOther": {
      const isOther = value === "Other";
      input = (
        <>
          <Select
            className="hod-input"
            classNames={{ popup: { root: "hod-popup" } }}
            placeholder="Select"
            value={value}
            disabled={disabled}
            options={[...field.options.map((o) => ({ label: o, value: o })), { label: "Other", value: "Other" }]}
            onChange={onChange}
          />
          {isOther && (
            <>
              <Input
                className="hod-input hod-input-mt"
                placeholder="Specify"
                value={values[`${field.name}Other`] || ""}
                disabled={disabled}
                status={errors?.[`${field.name}Other`] ? "error" : undefined}
                onChange={(e) => setValue(`${field.name}Other`, e.target.value)}
              />
              {errors?.[`${field.name}Other`] && (
                <div className="hod-field-error">{errors[`${field.name}Other`]}</div>
              )}
            </>
          )}
        </>
      );
      break;
    }
    case "yesno":
      input = (
        <Radio.Group
          className="hod-radio-group"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        >
          <Radio value="Yes">Yes</Radio>
          <Radio value="No">No</Radio>
        </Radio.Group>
      );
      break;
    case "yesnotext":
      input = (
        <>
          <Radio.Group
            className="hod-radio-group"
            value={value?.answer}
            disabled={disabled}
            onChange={(e) => onChange({ ...value, answer: e.target.value })}
          >
            <Radio value="Yes">Yes</Radio>
            <Radio value="No">No</Radio>
          </Radio.Group>
          {value?.answer === "Yes" && (
            <Input
              className="hod-input hod-input-mt"
              placeholder={field.notePlaceholder || "Notes"}
              value={value?.notes || ""}
              disabled={disabled}
              onChange={(e) => onChange({ ...value, notes: e.target.value })}
            />
          )}
        </>
      );
      break;
    case "geo":
      input = <GeoCapture value={value} onChange={onChange} disabled={disabled} />;
      break;
    case "repeatable":
      input = <GstinRepeater value={value} onChange={onChange} disabled={disabled} />;
      break;
    case "file":
      input = <FileField value={value} onChange={onChange} disabled={disabled} uploadType={field.uploadType} />;
      break;
    default:
      input = null;
  }

  return (
    <div className={cx("hod-field", (field.type === "longtext" || field.type === "file" || field.fullWidth) && "hod-field--full")}>
      <label className="hod-field-label">
        {field.label}
        {isRequired(field, values) && <span className="hod-required">*</span>}
      </label>
      {input}
      {fieldErrorText ? (
        <div className="hod-field-error">{fieldErrorText}</div>
      ) : (
        field.note && <div className="hod-field-note">{field.note}</div>
      )}
    </div>
  );
}

function getContacts(values, roleKey) {
  return values[roleKey] && values[roleKey].length ? values[roleKey] : [{}];
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function PocGroupField({ values, errors, onUpdateContact, onAddContact, onRemoveContact, disabled }) {
  return (
    <div className="hod-field hod-field--full hod-poc-group">
      {POC_ROLES.map((role) => {
        const contacts = getContacts(values, role.key);
        return (
          <div className="hod-poc-row" key={role.key}>
            <div className="hod-poc-role">
              {role.label}
              {role.required && <span className="hod-required">*</span>}
              {role.note && <div className="hod-field-note">{role.note}</div>}
            </div>
            {contacts.map((contact, idx) => {
              const emailInvalid = idx === 0 && !!contact.email && !EMAIL_PATTERN.test(contact.email);
              const phoneInvalid = idx === 0 && !!contact.phone && contact.phone.length !== 10;
              return (
              <div className="hod-poc-inputs" key={idx}>
                <Input
                  className="hod-input"
                  placeholder="Name"
                  disabled={disabled}
                  status={idx === 0 && errors?.[`${role.key}:name`] ? "error" : undefined}
                  value={contact.name || ""}
                  onChange={(e) => onUpdateContact(role.key, idx, "name", e.target.value)}
                />
                <Input
                  className="hod-input"
                  placeholder="Email"
                  disabled={disabled}
                  status={emailInvalid || (idx === 0 && errors?.[`${role.key}:email`]) ? "error" : undefined}
                  value={contact.email || ""}
                  onChange={(e) => onUpdateContact(role.key, idx, "email", e.target.value)}
                />
                <Input
                  className="hod-input"
                  placeholder="Phone"
                  disabled={disabled}
                  maxLength={10}
                  status={phoneInvalid || (idx === 0 && errors?.[`${role.key}:phone`]) ? "error" : undefined}
                  value={contact.phone || ""}
                  onChange={(e) => onUpdateContact(role.key, idx, "phone", e.target.value.replace(/\D/g, "").slice(0, 10))}
                  onKeyDown={(e) => {
                    const allowedControlKeys = ["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"];
                    if (e.ctrlKey || e.metaKey || allowedControlKeys.includes(e.key) || /^[0-9]$/.test(e.key)) return;
                    e.preventDefault();
                  }}
                />
                {role.designation && (
                  <Input
                    className="hod-input"
                    placeholder="Designation"
                    disabled={disabled}
                    value={contact.designation || ""}
                    onChange={(e) => onUpdateContact(role.key, idx, "designation", e.target.value)}
                  />
                )}
                {contacts.length > 1 && (
                  <Button
                    type="text"
                    danger
                    disabled={disabled}
                    className="hod-poc-remove-btn"
                    icon={<DeleteOutlined />}
                    onClick={() => onRemoveContact(role.key, idx)}
                  />
                )}
              </div>
              );
            })}
            <Button
              type="link"
              className="hod-add-link"
              disabled={disabled}
              icon={<PlusOutlined />}
              onClick={() => onAddContact(role.key)}
            >
              Add another {role.label}
            </Button>
          </div>
        );
      })}
    </div>
  );
}

function PropertyGroupField({ values, errors, onUpdateProperty, onAddProperty, onRemoveProperty, disabled }) {
  const properties = getProperties(values);
  return (
    <div className="hod-field hod-field--full">
      {properties.map((prop, idx) => (
        <div className="hod-property-card" key={idx}>
          <div className="hod-property-card-header">
            <span className="hod-property-card-title">Hotel {idx + 1}</span>
            {properties.length > 1 && (
              <Button
                type="text"
                danger
                size="small"
                disabled={disabled}
                icon={<DeleteOutlined />}
                onClick={() => onRemoveProperty(idx)}
              />
            )}
          </div>
          <div className="hod-property-card-grid">
            {PROPERTY_FIELDS.map((f) => {
              if (!isFieldVisible(f, prop)) return null;
              const value = prop[f.name];
              const onChange = (v) => onUpdateProperty(idx, f.name, v);
              const fieldErrorText = errors?.[`properties:${idx}:${f.name}`];

              let input = null;
              switch (f.type) {
                case "text":
                  input = (
                    <Input
                      className="hod-input"
                      placeholder={f.placeholder}
                      value={value || ""}
                      disabled={disabled}
                      onChange={(e) => onChange(e.target.value)}
                    />
                  );
                  break;
                case "number":
                  input = (
                    <InputNumber
                      className="hod-input hod-input-number"
                      value={value}
                      disabled={disabled}
                      onChange={onChange}
                    />
                  );
                  break;
                case "longtext":
                  input =
                    f.name === "registeredBillingAddress" ? (
                      <>
                        <Checkbox
                          className="hod-same-as-checkbox"
                          checked={!!prop.sameAsPropertyAddress}
                          disabled={disabled}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            onUpdateProperty(idx, "sameAsPropertyAddress", checked);
                            if (checked) onUpdateProperty(idx, "registeredBillingAddress", prop.propertyAddress || "");
                          }}
                        >
                          Same as Property Address
                        </Checkbox>
                        <Input.TextArea
                          className="hod-input hod-input-mt"
                          rows={3}
                          value={value || ""}
                          disabled={disabled || !!prop.sameAsPropertyAddress}
                          onChange={(e) => onChange(e.target.value)}
                        />
                      </>
                    ) : (
                      <Input.TextArea
                        className="hod-input"
                        rows={3}
                        value={value || ""}
                        disabled={disabled}
                        onChange={(e) => onChange(e.target.value)}
                      />
                    );
                  break;
                case "select":
                  input = (
                    <Select
                      className="hod-input"
                      classNames={{ popup: { root: "hod-popup" } }}
                      placeholder="Select"
                      value={value}
                      disabled={disabled}
                      options={f.options.map((o) => ({ label: o, value: o }))}
                      onChange={onChange}
                    />
                  );
                  break;
                case "selectOther": {
                  const isOther = value === "Other";
                  input = (
                    <>
                      <Select
                        className="hod-input"
                        classNames={{ popup: { root: "hod-popup" } }}
                        placeholder="Select"
                        value={value}
                        disabled={disabled}
                        options={[...f.options.map((o) => ({ label: o, value: o })), { label: "Other", value: "Other" }]}
                        onChange={onChange}
                      />
                      {isOther && (
                        <Input
                          className="hod-input hod-input-mt"
                          placeholder="Specify"
                          value={prop[`${f.name}Other`] || ""}
                          disabled={disabled}
                          onChange={(e) => onUpdateProperty(idx, `${f.name}Other`, e.target.value)}
                        />
                      )}
                    </>
                  );
                  break;
                }
                default:
                  input = null;
              }

              return (
                <div className={cx("hod-field", f.type === "longtext" && "hod-field--full")} key={f.name}>
                  <label className="hod-field-label">
                    {f.label}
                    {isRequired(f, prop) && <span className="hod-required">*</span>}
                  </label>
                  {input}
                  {fieldErrorText ? (
                    <div className="hod-field-error">{fieldErrorText}</div>
                  ) : (
                    f.note && <div className="hod-field-note">{f.note}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
      <Button type="link" className="hod-add-link" disabled={disabled} icon={<PlusOutlined />} onClick={onAddProperty}>
        Add Hotel
      </Button>
    </div>
  );
}

export default function HotelOnboardingDrawer({ open, onClose, hotelName, onComplete }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [tabsScroll, setTabsScroll] = useState({ atStart: true, atEnd: true });
  const [recordId, setRecordId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const tabsRef = useRef(null);
  const visibleSections = useMemo(
    () => SECTIONS.filter((s) => s.key !== "outcome" || values.onboardingSource === "Finkraft-assisted"),
    [values.onboardingSource]
  );
  const clampedStepIndex = Math.min(stepIndex, visibleSections.length - 1);
  const section = visibleSections[clampedStepIndex];
  const isSkipped = false;

  function updateTabsScroll() {
    const el = tabsRef.current;
    if (!el) return;
    setTabsScroll({
      atStart: el.scrollLeft <= 1,
      atEnd: el.scrollLeft + el.clientWidth >= el.scrollWidth - 1,
    });
  }

  useEffect(() => {
    updateTabsScroll();
  }, [stepIndex, open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    async function fetchExisting() {
      setLoadingExisting(true);
      try {
        const res = await fetch(API_ONBOARDING_DATA, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            hotelName,
            gstin: localStorage.getItem("hotelGstin"),
            domain: getSubdomain(),
          }),
        });
        const result = await res.json().catch(() => null);
        if (cancelled) return;
        const record = result?.data;
        const id = record?.id || record?._id;
        const loginGstin = localStorage.getItem("hotelGstin");
        if (record && id) {
          setRecordId(id);
          const hydrated = hydrateValues(record.values || {});
          if ((!hydrated.gstins || !hydrated.gstins.filter(Boolean).length) && loginGstin) {
            hydrated.gstins = [loginGstin];
          }
          setValues(hydrated);
          setStepIndex(record.stepIndex || 0);
        } else {
          setRecordId(null);
          setValues(loginGstin ? { gstins: [loginGstin] } : {});
          setStepIndex(0);
        }
      } catch (error) {
        // Existing-record lookup is best-effort; fall back to a fresh form.
      } finally {
        if (!cancelled) setLoadingExisting(false);
      }
    }
    async function fetchLegalName(loginGstin) {
      if (!loginGstin) return null;
      try {
        const res = await fetch(API_ONBOARDING_LEGAL_NAME, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ gstin: loginGstin }),
        });
        const result = await res.json().catch(() => null);
        // TODO: confirm the exact response field once the real payload shape
        // is shared — currently trying the common candidates.
        return (
          result?.data?.legalName ||
          result?.data?.legal_name ||
          result?.legalName ||
          result?.legal_name ||
          null
        );
      } catch (error) {
        // Legal-name lookup is best-effort; leave the field blank for manual entry.
        console.error("Legal name lookup failed:", error);
        return null;
      }
    }
    fetchExisting().then(async () => {
      if (cancelled) return;
      const legalName = await fetchLegalName(localStorage.getItem("hotelGstin"));
      if (cancelled || !legalName) return;
      setValues((prev) =>
        prev.legalBillingEntityName ? prev : { ...prev, legalBillingEntityName: legalName }
      );
    });
    return () => {
      cancelled = true;
    };
  }, [open, hotelName]);

  useEffect(() => {
    // PAN is embedded in a GSTIN (state code + 10-char PAN + entity code +
    // "Z" + checksum) — no API call needed, just slice it out.
    const firstGstin = values.gstins?.[0];
    if (values.pan || !firstGstin || !GSTIN_PATTERN.test(firstGstin)) return;
    setValues((prev) => (prev.pan ? prev : { ...prev, pan: firstGstin.slice(2, 12) }));
  }, [values.gstins]);

  useEffect(() => {
    // GSTIN Validated defaults to "Auto" (system checks GSTIN format) rather
    // than forcing a manual Yes/No choice; the person can still override it.
    if (values.gstinValidated !== undefined) return;
    setValues((prev) => (prev.gstinValidated !== undefined ? prev : { ...prev, gstinValidated: "Auto" }));
  }, [values.gstinValidated]);

  const requiredNames = useMemo(() => flattenRequiredFieldNames(section, values), [section, values]);
  const filledCount = requiredNames.filter((n) => {
    const v = getFieldValue(values, n);
    return v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0);
  }).length;
  const percentComplete = isSkipped
    ? 100
    : requiredNames.length
    ? Math.round((filledCount / requiredNames.length) * 100)
    : 100;

  const overallPercentComplete = useMemo(() => {
    let total = 0;
    let filled = 0;
    visibleSections.forEach((s) => {
      const names = flattenRequiredFieldNames(s, values);
      total += names.length;
      filled += names.filter((n) => {
        const v = getFieldValue(values, n);
        return v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0);
      }).length;
    });
    return total ? Math.round((filled / total) * 100) : 100;
  }, [visibleSections, values]);

  function setValue(name, val) {
    setValues((prev) => {
      const next = { ...prev, [name]: val };
      if (name === "onboardingSource") {
        if (val === SELF_ONBOARDED_VALUE) {
          next.finkraftPocName = SELF_ONBOARDED_NAME;
          next.finkraftPocEmail = "";
          next.finkraftPocPhone = "";
        } else if (prev.finkraftPocName === SELF_ONBOARDED_NAME) {
          next.finkraftPocName = "";
        }
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  }

  function updateContact(roleKey, index, field, val) {
    setValues((prev) => {
      const list = getContacts(prev, roleKey).map((c) => ({ ...c }));
      list[index] = { ...list[index], [field]: val };
      return { ...prev, [roleKey]: list };
    });
    setErrors((prev) => ({ ...prev, [`${roleKey}:${field}`]: undefined }));
  }

  function addContact(roleKey) {
    setValues((prev) => ({ ...prev, [roleKey]: [...getContacts(prev, roleKey), {}] }));
  }

  function removeContact(roleKey, index) {
    setValues((prev) => {
      const list = getContacts(prev, roleKey);
      if (list.length <= 1) return prev;
      return { ...prev, [roleKey]: list.filter((_, i) => i !== index) };
    });
  }

  function updateProperty(index, field, val) {
    setValues((prev) => {
      const list = getProperties(prev).map((p) => ({ ...p }));
      const updated = { ...list[index], [field]: val };
      if (field === "propertyAddress" && updated.sameAsPropertyAddress) {
        updated.registeredBillingAddress = val;
      }
      list[index] = updated;
      return { ...prev, properties: list };
    });
    setErrors((prev) => ({ ...prev, [`properties:${index}:${field}`]: undefined }));
  }

  function addProperty() {
    setValues((prev) => ({ ...prev, properties: [...getProperties(prev), {}] }));
  }

  function removeProperty(index) {
    setValues((prev) => {
      const list = getProperties(prev);
      if (list.length <= 1) return prev;
      return { ...prev, properties: list.filter((_, i) => i !== index) };
    });
  }

  function validateSection() {
    if (isSkipped) return true;
    const nextErrors = {};
    section.fields.forEach((f) => {
      if (f.type === "divider" || f.type === "pocGroup" || f.type === "propertyGroup") return;
      if (!isFieldVisible(f, values)) return;
      const v = values[f.name];
      const isEmpty = v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
      if (isRequired(f, values) && isEmpty) {
        nextErrors[f.name] = "Required";
      } else if (f.type === "email" && v && !EMAIL_PATTERN.test(v)) {
        nextErrors[f.name] = "Invalid email";
      } else if (f.type === "repeatable" && f.name === "gstins" && Array.isArray(v) && v.some((g) => g && !GSTIN_PATTERN.test(g))) {
        nextErrors[f.name] = "Invalid GSTIN format";
      }
      if (f.type === "selectOther" && isRequired(f, values) && v === "Other" && !values[`${f.name}Other`]) {
        nextErrors[`${f.name}Other`] = "Required";
      }
    });
    if (section.key === "contacts") {
      POC_ROLES.forEach((role) => {
        const first = getContacts(values, role.key)[0];
        if (role.required) {
          ["name", "email", "phone"].forEach((suffix) => {
            if (!first[suffix]) nextErrors[`${role.key}:${suffix}`] = "Required";
          });
        }
        if (first.email && !EMAIL_PATTERN.test(first.email)) {
          nextErrors[`${role.key}:email`] = "Invalid email";
        }
        if (first.phone && first.phone.length !== 10) {
          nextErrors[`${role.key}:phone`] = "Invalid phone";
        }
      });
    }
    if (section.key === "identity") {
      getProperties(values).forEach((prop, idx) => {
        PROPERTY_FIELDS.forEach((pf) => {
          if (!isFieldVisible(pf, prop)) return;
          const v = prop[pf.name];
          const isEmpty = v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
          if (isRequired(pf, prop) && isEmpty) {
            nextErrors[`properties:${idx}:${pf.name}`] = "Required";
          }
          if (pf.type === "selectOther" && isRequired(pf, prop) && v === "Other" && !prop[`${pf.name}Other`]) {
            nextErrors[`properties:${idx}:${pf.name}Other`] = "Required";
          }
        });
      });
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  function handleBack() {
    if (clampedStepIndex === 0) return;
    setStepIndex(clampedStepIndex - 1);
  }

  async function saveProgress(isFinal) {
    setSaving(true);
    try {
      const res = await fetch(API_ONBOARDING_SAVE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          _id: recordId,
          hotelName,
          gstin: localStorage.getItem("hotelGstin"),
          domain: getSubdomain(),
          sectionKey: section.key,
          stepIndex: clampedStepIndex,
          completed: !!isFinal,
          values,
        }),
      });
      const result = await res.json().catch(() => null);
      if (!res.ok || result?.success === false) {
        message.error(result?.message || "Failed to save. Please try again.");
        return false;
      }
      const savedId = result?.data?._id || result?.data?.id || result?._id || result?.id;
      if (savedId && !recordId) setRecordId(savedId);
      return true;
    } catch (error) {
      message.error("Failed to save. Please try again.");
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveContinue() {
    if (!validateSection()) {
      message.error("Please fill all mandatory fields, or mark this section as not known / not shared.");
      return;
    }
    const isLastStep = clampedStepIndex === visibleSections.length - 1;
    const saved = await saveProgress(isLastStep);
    if (!saved) return;
    if (isLastStep) {
      onComplete?.(values);
      message.success("Hotel onboarding saved.");
      onClose?.();
      return;
    }
    setStepIndex(clampedStepIndex + 1);
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={700}
      closable={false}
      maskClosable={!saving && !loadingExisting}
      keyboard={!saving && !loadingExisting}
      className="hod-drawer"
      styles={{ body: { padding: 0 } }}
    >
      <div className="hod-header">
        <Button
          type="text"
          className="hod-close-btn"
          icon={<CloseOutlined />}
          disabled={saving}
          onClick={onClose}
        />
        <div className="hod-eyebrow">HOTEL SETUP</div>
        <div className="hod-title">Onboarding Form</div>
        <div className="hod-subtitle">{hotelName || "New Hotel"}</div>
      </div>

      <div className="hod-progress-bar-row">
        <span>Overall Progress</span>
        <span className="hod-progress-pct" style={{ color: getProgressColor(overallPercentComplete) }}>
          {overallPercentComplete}% complete
        </span>
      </div>
      <div className="hod-progress-track">
        <div
          className="hod-progress-fill"
          style={{ width: `${overallPercentComplete}%`, background: getProgressColor(overallPercentComplete) }}
        />
      </div>

      <div className="hod-progress-bar-row hod-progress-bar-row--step">
        <span>
          Step {clampedStepIndex + 1} of {visibleSections.length} — <b>{section.title}</b>
        </span>
        <span className="hod-progress-pct" style={{ color: getProgressColor(percentComplete) }}>
          {percentComplete}% complete
        </span>
      </div>
      <div className="hod-progress-track hod-progress-track--step">
        <div
          className="hod-progress-fill"
          style={{ width: `${percentComplete}%`, background: getProgressColor(percentComplete) }}
        />
      </div>

      <div
        className={cx(
          "hod-tabs-wrap",
          !tabsScroll.atStart && "hod-tabs-wrap--fade-left",
          !tabsScroll.atEnd && "hod-tabs-wrap--fade-right"
        )}
      >
        <div className="hod-tabs" ref={tabsRef} onScroll={updateTabsScroll}>
          {visibleSections.map((s, i) => {
            const done = i !== clampedStepIndex && isSectionComplete(s, values);
            const active = i === clampedStepIndex;
            return (
              <button
                key={s.key}
                className={cx("hod-tab", active && "hod-tab--active", done && "hod-tab--done")}
                onClick={() => setStepIndex(i)}
              >
                <span className="hod-tab-index">
                  {done ? <CheckOutlined /> : i + 1}
                </span>
                {s.shortTitle}
              </button>
            );
          })}
        </div>
      </div>

      <div className="hod-content">
        <div className="hod-section-desc">{section.desc}</div>

        <div className="hod-fields-grid">
          {section.fields.map((f) =>
            f.type === "divider" ? (
              <div className="hod-divider" key={f.name}>
                {f.label}
              </div>
            ) : f.type === "pocGroup" ? (
              <PocGroupField
                key={f.name}
                values={values}
                errors={errors}
                onUpdateContact={updateContact}
                onAddContact={addContact}
                onRemoveContact={removeContact}
                disabled={isSkipped}
              />
            ) : f.type === "propertyGroup" ? (
              <PropertyGroupField
                key={f.name}
                values={values}
                errors={errors}
                onUpdateProperty={updateProperty}
                onAddProperty={addProperty}
                onRemoveProperty={removeProperty}
                disabled={isSkipped}
              />
            ) : (
              <Field key={f.name} field={f} values={values} setValue={setValue} disabled={isSkipped} errors={errors} />
            )
          )}
        </div>
        {Object.keys(errors).some((k) => errors[k]) && (
          <div className="hod-error-banner">Some mandatory fields above are missing.</div>
        )}
      </div>

      <div className="hod-footer">
        <Button
          className="hod-back-btn"
          disabled={clampedStepIndex === 0 || saving || loadingExisting}
          onClick={handleBack}
        >
          ← Back
        </Button>
        <Button
          type="primary"
          className="hod-continue-btn"
          loading={saving}
          disabled={loadingExisting}
          onClick={handleSaveContinue}
        >
          {clampedStepIndex === visibleSections.length - 1 ? "Submit" : "Save & Continue →"}
        </Button>
      </div>
    </Drawer>
  );
}
